import { useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

import Sidebar from './components/Sidebar.jsx';
import TopBar from './components/TopBar.jsx';
import JobCenter from './components/JobCenter.jsx';
import RequireAuth from './components/RequireAuth.jsx';
import { useAuth } from './context/AuthContext.jsx';

import Landing from './pages/Landing.jsx';
import Login from './pages/Login.jsx';
import Signup from './pages/Signup.jsx';
import ForgotPassword from './pages/ForgotPassword.jsx';

import Overview from './modules/Overview.jsx';
import Briefings from './modules/Briefings/Briefings.jsx';
import Screener from './modules/Screener/Screener.jsx';
import PortfolioBacktest from './modules/Portfolio/PortfolioBacktest.jsx';
import BacktestWorkspace from './modules/Backtest/BacktestWorkspace.jsx';
import Research from './modules/Research/Research.jsx';
import Strategies from './modules/Strategies/Strategies.jsx';
import Pricing from './modules/Pricing.jsx';

function useTheme() {
  const [theme, setTheme] = useState(
    () => localStorage.getItem('aq-theme') || 'light'
  );

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('aq-theme', theme);
  }, [theme]);

  return [theme, () => setTheme((t) => (t === 'light' ? 'dark' : 'light'))];
}

// Public routes (/, /login, /signup, /forgot-password) redirect an already
// signed-in seat straight into /app, so a bookmarked auth page doesn't
// dead-end someone who's already logged in. Landing itself stays open to
// signed-in visitors (it's the marketing front door), so it isn't wrapped.
function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/app" replace />;
  return children;
}

// The authenticated dashboard shell — same Sidebar + TopBar layout as
// before, now mounted at /app/* with its own nested <Routes>.
function DashboardShell() {
  const { user } = useAuth();
  const [theme, toggleTheme] = useTheme();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  return (
    <div className="aq-app">
      <Sidebar mobileOpen={mobileNavOpen} onClose={() => setMobileNavOpen(false)} />
      <div className="aq-main">
        <TopBar
          onMenuClick={() => setMobileNavOpen((v) => !v)}
          theme={theme}
          onToggleTheme={toggleTheme}
        />
        <main className="aq-content">
          <JobCenter />
          <Routes key={user?.uid || 'signed-out'}>
            <Route index element={<Overview />} />
            <Route path="briefings" element={<Briefings />} />
            <Route path="screener" element={<Screener />} />
            <Route path="portfolio" element={<PortfolioBacktest />} />
            <Route path="backtest" element={<BacktestWorkspace />} />
            <Route path="research" element={<Research />} />
            <Route path="strategies" element={<Strategies />} />
            <Route path="billing" element={<Pricing />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route
        path="/login"
        element={
          <PublicOnly>
            <Login />
          </PublicOnly>
        }
      />
      <Route
        path="/signup"
        element={
          <PublicOnly>
            <Signup />
          </PublicOnly>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicOnly>
            <ForgotPassword />
          </PublicOnly>
        }
      />
      <Route
        path="/app/*"
        element={
          <RequireAuth>
            <DashboardShell />
          </RequireAuth>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
